import React, { useState } from 'react';
import { Layout, Menu, Button, Avatar, Dropdown, Tag, message } from 'antd';
import {
  DashboardOutlined, SafetyCertificateOutlined, ShoppingOutlined,
  DollarOutlined, ShoppingCartOutlined, CarOutlined, UserOutlined,
  SettingOutlined, CustomerServiceOutlined, BarChartOutlined,
  TeamOutlined, LogoutOutlined, MenuFoldOutlined, MenuUnfoldOutlined,
  BellOutlined, HomeOutlined, ShopOutlined, AppstoreOutlined,
} from '@ant-design/icons';
import { BRAND } from './constants/images';
import { ROLE_LABELS } from './constants/roles';
import { signInWithGoogle, signOutAdmin, checkAdminAccess } from './services/firebase';
import Dashboard from './pages/analytics/Dashboard';
import KYCQueue from './pages/kyc/KYCQueue';
import ProductApproval from './pages/products/ProductApproval';
import DealerProductApproval from './pages/products/DealerProductApproval';
import CommissionConfig from './pages/config/CommissionConfig';
import FinanceDashboard from './pages/finance/FinanceDashboard';
import OrderManagement from './pages/orders/OrderManagement';
import UserManagement from './pages/users/UserManagement';
import DealerManagement from './pages/users/DealerManagement';
import LeadManagement from './pages/leads/LeadManagement';
import ContractorAdmin from './pages/leads/ContractorAdmin';
import LogisticsControl from './pages/logistics/LogisticsControl';
import SupportDashboard from './pages/support/SupportDashboard';
import AnalyticsReports from './pages/analytics/AnalyticsReports';
import HomeOwnerManagement from './pages/homeowners/HomeOwnerManagement';
import './App.css';

const { Header, Sider, Content } = Layout;

const PlaceholderPage = ({ title, subtitle }) => (
  <div style={{
    display: 'flex', flexDirection: 'column',
    alignItems: 'center', justifyContent: 'center',
    minHeight: '60vh', gap: 16,
  }}>
    <div style={{ fontSize: 24, fontWeight: 600, color: '#C9A84C' }}>{title}</div>
    <div style={{ fontSize: 14, color: '#7A8A96' }}>{subtitle}</div>
    <div style={{
      background: 'rgba(201,168,76,0.1)',
      border: '0.5px solid rgba(201,168,76,0.3)',
      borderRadius: 8, padding: '8px 20px',
      fontSize: 12, color: '#9A7A2E',
    }}>
      Building this module now...
    </div>
  </div>
);

const LoginPage = ({ onLogin }) => {
  const [loading, setLoading] = useState(false);

  const handleGoogleLogin = async () => {
    setLoading(true);
    try {
      const { user, error } = await signInWithGoogle();
      if (error) {
        message.error('Google sign-in failed. Try again.');
        setLoading(false);
        return;
      }
      const { allowed, adminData } = await checkAdminAccess(user.email);
      if (!allowed) {
        message.error('Access Denied - You are not authorised to access this panel.');
        await signOutAdmin();
        setLoading(false);
        return;
      }
      message.success('Welcome back, ' + adminData.name + '!');
      onLogin({
        name: adminData.name,
        email: adminData.email,
        role: adminData.role,
        photo: user.photoURL,
      });
    } catch (err) {
      message.error('Something went wrong. Please try again.');
    }
    setLoading(false);
  };

  return (
    <div className="ks-login-page">
      <div className="ks-login-card">
        <div className="ks-login-logo">
          <img src={BRAND.logoIcon} alt="KamSathi Logo" className="ks-icon" />
          <img src={BRAND.logoText} alt="KamSathi" className="ks-wordmark" />
          <div className="ks-panel-label">Admin Panel</div>
        </div>
        <div className="ks-login-title">
          Welcome - Restricted Access Only
        </div>
        <div style={{ height: '0.5px', background: 'rgba(201,168,76,0.25)', marginBottom: 28 }} />
        <Button
          size="large"
          onClick={handleGoogleLogin}
          loading={loading}
          style={{
            width: '100%', background: '#222222',
            border: '1px solid rgba(201,168,76,0.4)',
            color: '#D4DCE3', borderRadius: 8, height: 48,
            display: 'flex', alignItems: 'center',
            justifyContent: 'center', gap: 10,
            fontSize: 14, marginBottom: 16,
          }}
        >
          {!loading && (
            <img src="https://www.google.com/favicon.ico" alt="Google"
              style={{ width: 18, height: 18 }} />
          )}
          Continue with Google
        </Button>
        <div style={{
          textAlign: 'center', fontSize: 11,
          color: '#7A8A96', marginTop: 8, lineHeight: 1.6,
        }}>
          Secured access - KamSathi team only<br />
          Unauthorized access is strictly prohibited
        </div>
        <div style={{
          textAlign: 'center', fontSize: 11, color: '#4A4A47',
          marginTop: 24, paddingTop: 16,
          borderTop: '0.5px solid rgba(201,168,76,0.15)',
        }}>
          {BRAND.tagline}
        </div>
      </div>
    </div>
  );
};

const AdminLayout = ({ user, onLogout }) => {
  const [collapsed, setCollapsed] = useState(false);
  const [currentPage, setCurrentPage] = useState('analytics');

  const menuItems = [
    { key: 'analytics',         icon: <DashboardOutlined />,        label: 'Dashboard'              },
    { key: 'kyc',               icon: <SafetyCertificateOutlined />, label: 'KYC & Verification'     },
    { key: 'products',          icon: <ShoppingOutlined />,          label: 'Products & Listings'    },

    // ── NEW: Dealer App sections ──
    {
      key: 'dealer_group',
      icon: <ShopOutlined />,
      label: 'Dealer App',
      children: [
        { key: 'dealer_management', icon: <ShopOutlined />,     label: 'Dealer Management'  },
        { key: 'dealer_products',   icon: <AppstoreOutlined />, label: 'Dealer Products'    },
      ],
    },

    { key: 'config',            icon: <SettingOutlined />,           label: 'Commission & Config'    },
    { key: 'orders',            icon: <ShoppingCartOutlined />,      label: 'Order Management'       },
    { key: 'finance',           icon: <DollarOutlined />,            label: 'Finance & Payouts'      },
    { key: 'users',             icon: <UserOutlined />,              label: 'User Management'        },
    { key: 'homeowners',        icon: <HomeOutlined />,              label: 'HomeOwner App'          },
    { key: 'leads',             icon: <TeamOutlined />,              label: 'Leads & Contractors'    },
    { key: 'contractor_admin',  icon: <SafetyCertificateOutlined />, label: 'Contractor Admin'       },
    { key: 'logistics',         icon: <CarOutlined />,               label: 'Logistics Control'      },
    { key: 'support',           icon: <CustomerServiceOutlined />,   label: 'Support & Disputes'     },
    { key: 'reports',           icon: <BarChartOutlined />,          label: 'Analytics & Reports'    },
  ];

  const PAGE_CONFIG = {
    analytics:          { title: 'Dashboard',              subtitle: 'Live overview of KamSathi business'                          },
    kyc:                { title: 'KYC & Verification',     subtitle: 'Review and approve dealer, builder, agent KYC'               },
    products:           { title: 'Products & Listings',    subtitle: 'Approve products, assign tiers'                              },
    dealer_management:  { title: 'Dealer Management',      subtitle: 'Approve, reject and manage KamSathi dealers'                 },
    dealer_products:    { title: 'Dealer Products',        subtitle: 'Review dealer product submissions, assign tiers'             },
    config:             { title: 'Commission & Config',    subtitle: 'Set commission rates and business rules'                     },
    orders:             { title: 'Order Management',       subtitle: 'Monitor and manage all orders'                               },
    finance:            { title: 'Finance & Payouts',      subtitle: 'GMV, payouts, GST reports'                                   },
    users:              { title: 'User Management',        subtitle: 'Manage all users across all apps'                            },
    homeowners:         { title: 'HomeOwner App',          subtitle: 'Manage homeowners and their jobs'                            },
    leads:              { title: 'Leads & Contractors',    subtitle: 'Job leads, pricing, contractor management'                   },
    logistics:          { title: 'Logistics Control',      subtitle: 'Live delivery map, agents, zones'                            },
    support:            { title: 'Support & Disputes',     subtitle: 'Tickets, disputes, fraud control'                            },
    contractor_admin:   { title: 'Contractor Admin',       subtitle: 'Full contractor app management'                              },
    reports:            { title: 'Analytics & Reports',    subtitle: 'Category intelligence, seller scorecards'                    },
  };

  const handleLogout = async () => {
    await signOutAdmin();
    onLogout();
    message.success('Logged out successfully');
  };

  const userMenuItems = [
    { key: 'profile', icon: <UserOutlined />, label: 'My Profile' },
    { type: 'divider' },
    { key: 'logout', icon: <LogoutOutlined />, label: 'Logout', danger: true, onClick: handleLogout },
  ];

  const currentConfig = PAGE_CONFIG[currentPage] || PAGE_CONFIG.analytics;

  const renderPage = () => {
    switch (currentPage) {
      case 'analytics':         return <Dashboard />;
      case 'kyc':               return <KYCQueue />;
      case 'products':          return <ProductApproval />;
      case 'dealer_management': return <DealerManagement />;
      case 'dealer_products':   return <DealerProductApproval />;
      case 'config':            return <CommissionConfig />;
      case 'finance':           return <FinanceDashboard />;
      case 'orders':            return <OrderManagement />;
      case 'users':             return <UserManagement />;
      case 'homeowners':        return <HomeOwnerManagement />;
      case 'leads':             return <LeadManagement />;
      case 'logistics':         return <LogisticsControl />;
      case 'support':           return <SupportDashboard />;
      case 'contractor_admin':  return <ContractorAdmin />;
      case 'reports':           return <AnalyticsReports />;
      default:
        return <PlaceholderPage
          title={currentConfig.title}
          subtitle={currentConfig.subtitle}
        />;
    }
  };

  return (
    <Layout style={{ minHeight: '100vh' }}>
      <Sider
        trigger={null}
        collapsible
        collapsed={collapsed}
        width={240}
        style={{
          background: '#111111',
          borderRight: '0.5px solid rgba(201,168,76,0.2)',
          position: 'sticky', top: 0,
          height: '100vh', overflow: 'auto',
        }}
      >
        <div className="ks-sidebar-logo">
          <img src={BRAND.logoIcon} alt="KS" className="icon" />
          {!collapsed && <img src={BRAND.logoText} alt="KamSathi" className="wordmark" />}
        </div>
        {!collapsed && (
          <div style={{
            textAlign: 'center', fontSize: 10, color: '#7A8A96',
            letterSpacing: 2, textTransform: 'uppercase',
            padding: '4px 0 12px',
            borderBottom: '0.5px solid rgba(201,168,76,0.1)',
            marginBottom: 8,
          }}>
            Admin Panel
          </div>
        )}
        <Menu
          mode="inline"
          selectedKeys={[currentPage]}
          defaultOpenKeys={['dealer_group']}
          onClick={({ key }) => {
            if (key !== 'dealer_group') setCurrentPage(key);
          }}
          style={{ background: '#111111', border: 'none', color: '#7A8A96' }}
          items={menuItems}
        />
        {!collapsed && (
          <div style={{
            position: 'absolute', bottom: 16,
            left: 0, right: 0, textAlign: 'center',
            fontSize: 10, color: '#4A4A47',
          }}>
            v1.0.0 - KamSathi Admin
          </div>
        )}
      </Sider>

      <Layout>
        <Header
          className="ks-topbar"
          style={{ height: 56, lineHeight: '56px', padding: '0 24px' }}
        >
          <div style={{ display: 'flex', alignItems: 'center', gap: 16 }}>
            <Button
              type="text"
              icon={collapsed ? <MenuUnfoldOutlined /> : <MenuFoldOutlined />}
              onClick={() => setCollapsed(!collapsed)}
              style={{ color: '#C9A84C', fontSize: 16 }}
            />
            <span className="ks-topbar-title">{currentConfig.title}</span>
          </div>
          <div style={{ display: 'flex', alignItems: 'center', gap: 16 }}>
            <Button type="text" icon={<BellOutlined />}
              style={{ color: '#7A8A96', fontSize: 16 }} />
            <Tag style={{
              background: 'rgba(201,168,76,0.15)',
              border: '0.5px solid rgba(201,168,76,0.3)',
              color: '#C9A84C', borderRadius: 20, fontSize: 11,
            }}>
              {ROLE_LABELS[user.role]}
            </Tag>
            <Dropdown menu={{ items: userMenuItems }} placement="bottomRight">
              <div style={{
                display: 'flex', alignItems: 'center',
                gap: 8, cursor: 'pointer',
              }}>
                <Avatar
                  src={user.photo}
                  style={{
                    background: 'linear-gradient(135deg, #E2C170, #9A7A2E)',
                    color: '#0D0D0D', fontWeight: 600,
                  }}
                  size={32}
                >
                  {!user.photo && user.name.charAt(0)}
                </Avatar>
                <span style={{ fontSize: 13, color: '#D4DCE3' }}>
                  {user.name.split(' ')[0]}
                </span>
              </div>
            </Dropdown>
          </div>
        </Header>

        <Content className="ks-content">
          <div className="ks-page-header">
            <div className="ks-page-title">{currentConfig.title}</div>
            <div className="ks-page-subtitle">{currentConfig.subtitle}</div>
          </div>
          <div className="ks-divider" style={{ marginBottom: 24 }} />
          {renderPage()}
        </Content>
      </Layout>
    </Layout>
  );
};

export default function App() {
  const [user, setUser] = useState(null);
  const handleLogin  = (userData) => setUser(userData);
  const handleLogout = () => setUser(null);
  if (!user) return <LoginPage onLogin={handleLogin} />;
  return <AdminLayout user={user} onLogout={handleLogout} />;
}